import { lazy, useEffect, useMemo, useState, type ReactElement } from "react";
import { useLocation, useParams } from "react-router-dom";
import { PageData, apiGet } from "../../api";
import {
  spaCacheTtlMs,
  spaDashboardCacheInvalidate,
  spaPageDataCacheInvalidate,
  spaPageDataCachePeek,
  spaPageDataCachePut,
} from "../../spaDataCache";
import type { AppRoute } from "../../routes";
import { EmptyState } from "../../components/EmptyState";
import { PageSpinner } from "../../components/PageSpinner";
import { DesignedPage } from "../../routeModules/DesignedPage";
import { spaPageLoadErrorMessage } from "./spaPageLoadErrorMessage";
import { routeNeedsNativeData } from "./routeNeedsNativeData";
import { LegacyParityPage } from "./LegacyParityPage";
import { applySpaPagePayload } from "./applySpaPagePayload";
import { buildPageDataCacheKey } from "./buildPageDataCacheKey";

const MapViewPage = lazy(() => import("../../pages/inventory/MapViewPage").then((m) => ({ default: m.MapViewPage })));
const InventorySitePage = lazy(() => import("../../pages/inventory/InventorySitePage").then((m) => ({ default: m.InventorySitePage })));
const InventoryConfigPage = lazy(() =>
  import("../../pages/inventory/InventoryConfigPage").then((m) => ({ default: m.InventoryConfigPage }))
);
const MapConfigPage = lazy(() => import("../../pages/inventory/MapConfigPage").then((m) => ({ default: m.MapConfigPage })));
const AnalyticsPage = lazy(() => import("../../pages/analytics").then((m) => ({ default: m.AnalyticsPage })));
const AnalyticsConfigPage = lazy(() =>
  import("../../pages/analyticsConfig").then((m) => ({ default: m.AnalyticsConfigPage }))
);
const ConnectorPage = lazy(() => import("../../pages/connector").then((m) => ({ default: m.ConnectorPage })));
const TaskStatusPage = lazy(() => import("../../pages/inventory/TaskStatusPage").then((m) => ({ default: m.TaskStatusPage })));
const AlarmContactPage = lazy(() =>
  import("../../pages/alarmContact").then((m) => ({ default: m.AlarmContactPage }))
);
const FlowChatPage = lazy(() =>
  import("../../pages/flowChat/FlowChatPage").then((m) => ({ default: m.FlowChatPage }))
);

function inventoryMapScreen(route: AppRoute): ReactElement | null {
  const page: PageData = {
    route: route.id,
    title: route.title,
    description: route.description,
    datasets: [],
    actions: [],
    meta: {},
  };
  if (route.id === "map_view" || route.path === "/map") return <MapViewPage route={route} page={page} />;
  if (route.id === "inventory_site" || route.path === "/inventory/site") return <InventorySitePage route={route} page={page} />;
  if (route.id === "inventory_config" || route.path === "/inventory/config") return <InventoryConfigPage route={route} page={page} />;
  if (route.id === "map_config" || route.path === "/map-config") return <MapConfigPage route={route} page={page} />;
  if (route.id === "analytics" || route.path === "/analytics") return <AnalyticsPage route={route} page={page} />;
  if (route.id === "analytics_config" || route.path === "/analytics-config")
    return <AnalyticsConfigPage route={route} page={page} />;
  if (route.id === "connector" || route.path === "/connector") return <ConnectorPage route={route} page={page} />;
  if (route.id === "task_status" || route.path === "/task-status") return <TaskStatusPage route={route} page={page} />;
  return null;
}

function alarmContactScreen(route: AppRoute, page: PageData | null): ReactElement | null {
  if (route.id !== "alarm_contact" && route.path !== "/alarm-contacts") return null;
  const payload =
    page ||
    ({
      route: route.id,
      title: route.title,
      description: route.description,
      datasets: [],
      actions: [],
      meta: {},
    } satisfies PageData);
  return <AlarmContactPage route={route} page={payload} />;
}

export function Page({ route }: { route: AppRoute }) {
  const location = useLocation();
  const params = useParams();
  const paramsKey = JSON.stringify(params);
  const pageDataCacheKey = useMemo(
    () => buildPageDataCacheKey(location.search, paramsKey, route.id),
    [location.pathname, location.search, paramsKey, route.id]
  );
  const [data, setData] = useState<PageData | null>(() => {
    if (!routeNeedsNativeData(route.id)) return null;
    const hit = spaPageDataCachePeek(buildPageDataCacheKey(location.search, paramsKey, route.id));
    return hit ? hit.value : null;
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(() => {
    if (!routeNeedsNativeData(route.id)) return false;
    const hit = spaPageDataCachePeek(buildPageDataCacheKey(location.search, paramsKey, route.id));
    return !hit;
  });
  useEffect(() => {
    if (!routeNeedsNativeData(route.id)) {
      queueMicrotask(() => {
        setData(null);
        setError(null);
        setLoading(false);
      });
      return;
    }
    let cancelled = false;
    const expectedRoute = route.id;
    const ttl = spaCacheTtlMs();
    const hit = spaPageDataCachePeek(pageDataCacheKey);
    const age = hit ? Date.now() - hit.at : Infinity;
    const fresh = Boolean(hit && age < ttl);
    if (fresh) {
      setData(hit!.value);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    if (hit && age >= ttl) {
      setData(hit.value);
      setError(null);
      setLoading(false);
    }
    const needBlockingSpinner = !hit;
    if (needBlockingSpinner) {
      queueMicrotask(() => {
        setLoading(true);
        setError(null);
        setData(null);
      });
    }
    void apiGet<PageData>(pageDataCacheKey)
      .then((payload) => {
        if (cancelled) return;
        const applied = applySpaPagePayload(payload, expectedRoute, setData, setError);
        if (applied) spaPageDataCachePut(pageDataCacheKey, applied);
      })
      .catch((err) => {
        if (cancelled) return;
        if (!hit) setError(spaPageLoadErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pageDataCacheKey, route.id]);
  useEffect(() => {
    const refreshHandler = () => {
      if (!routeNeedsNativeData(route.id)) return;
      const expectedRoute = route.id;
      spaPageDataCacheInvalidate(pageDataCacheKey);
      if (expectedRoute === "dashboard") spaDashboardCacheInvalidate();
      void apiGet<PageData>(pageDataCacheKey)
        .then((payload) => {
          const applied = applySpaPagePayload(payload, expectedRoute, setData, setError);
          if (applied) spaPageDataCachePut(pageDataCacheKey, applied);
        })
        .catch((err) => setError(spaPageLoadErrorMessage(err)));
    };
    window.addEventListener("klt-rms-refresh-page", refreshHandler);
    return () => window.removeEventListener("klt-rms-refresh-page", refreshHandler);
  }, [pageDataCacheKey, route.id]);
  useEffect(() => {
    if (route.id !== "modbus_device_view" && route.id !== "modbus_device_view_machine") return;
    if (!routeNeedsNativeData(route.id)) return;
    const reload = () => {
      if (document.visibilityState !== "visible") return;
      const expectedRoute = route.id;
      void apiGet<PageData>(pageDataCacheKey).then((payload) => {
        const applied = applySpaPagePayload(payload, expectedRoute, setData, setError);
        if (applied) spaPageDataCachePut(pageDataCacheKey, applied);
      });
    };
    document.addEventListener("visibilitychange", reload);
    return () => document.removeEventListener("visibilitychange", reload);
  }, [pageDataCacheKey, route.id]);
  const inventoryScreen = inventoryMapScreen(route);
  if (inventoryScreen) return inventoryScreen;
  // Alarm Contact loads contacts from its own APIs; do not fall through to the empty React shell.
  const contactScreen = alarmContactScreen(route, data);
  if (contactScreen) return contactScreen;
  // FlowChat uses its own APIs; skip SPA page-data gate.
  if (route.id === "flow_chat") {
    return <FlowChatPage route={route} />;
  }
  if (loading) {
    return <PageSpinner />;
  }
  if (error) {
    return <EmptyState title="Unable to load page" description={error} tone="danger" />;
  }
  if (!routeNeedsNativeData(route.id)) {
    return <LegacyParityPage route={route} />;
  }
  const page = data || {
    route: route.id,
    title: route.title,
    description: route.description,
    datasets: [],
    actions: [],
    meta: {},
  };
  return <DesignedPage route={route} page={page} />;
}
